import styled from "@emotion/styled";
import React, { useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { ROUTES } from "@stores/AccountStore";
import { useStores } from "@stores";
import { useLang, useMoment } from "@src/hooks/useLang";
import useOnClickOutside from "@src/hooks/useOnClickOutside";
import { ReactComponent as MapIcon } from "@assets/icons/map.svg";
import { ReactComponent as RouterIcon } from "@assets/icons/router.svg";
import { ReactComponent as TrackIcon } from "@assets/icons/track.svg";
import { ReactComponent as TreeIcon } from "@assets/icons/tree.svg";
import { ReactComponent as MoreIcon } from "@assets/icons/more.svg";
import { ReactComponent as SettingsIcon } from "@assets/icons/settings.svg";
import { ReactComponent as ProfileIcon } from "@assets/icons/profile.svg";
import { ReactComponent as AnalyticsIcon } from "@assets/icons/analytics.svg";
import { ReactComponent as ReportIcon } from "@assets/icons/report.svg";
import { ReactComponent as AgentIcon } from "@assets/icons/agent.svg";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";
import Text from "@components/Text";
import { useFarmsVM } from "@stores/FarmStore";
import { useSeasonsVM } from "@stores/SeasonStore";
import { Season } from "@services/fieldsService";
import { canOpenRoute } from "@src/utils/navigation";
import {
  getInitials,
  Avatar,
  RoleBadge,
  membershipsWithSelf,
  canSwitchAccounts,
} from "@components/AccountSwitcher/shared";

export const TAB_BAR_HEIGHT = 56;
/* Total height the tab bar occupies = TAB_BAR_HEIGHT + the bottom inset below.
   Screens pad their scroll area by this so content clears the bar:
   calc(${TAB_BAR_HEIGHT}px + var(--tabbar-inset, env(safe-area-inset-bottom, 0px))) */

/* Anchored to the very bottom (bottom: 0) and the white background is extended
   DOWN through the safe-area / Safari browser-bar strip via padding-bottom, so
   no page content ever shows through beneath the bar. The tab row itself stays
   TAB_BAR_HEIGHT tall (content-box) and sits above that filled strip. */
const Root = styled.nav`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${TAB_BAR_HEIGHT}px;
  padding-bottom: var(--tabbar-inset, env(safe-area-inset-bottom, 0px));
  box-sizing: content-box;
  background: #ffffff;
  border-top: 1px solid rgba(137, 144, 147, 0.3);
  display: none;
  justify-content: space-around;
  align-items: center;
  z-index: 1000;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: flex;
  }
`;

const TabItem = styled(Link)<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  text-decoration: none;
  color: ${({ active }) => (active ? "#68ad18" : "#899093")};
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  gap: 2px;
  svg {
    width: 20px;
    height: 20px;
  }
  path {
    fill: ${({ active }) => (active ? "#68ad18" : "#899093")};
  }
`;

const MoreButton = styled.button<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  height: 100%;
  border: none;
  background: none;
  cursor: pointer;
  color: ${({ active }) => (active ? "#68ad18" : "#899093")};
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  gap: 2px;
  svg {
    width: 20px;
    height: 20px;
  }
  path {
    fill: ${({ active }) => (active ? "#68ad18" : "#899093")};
  }
`;

const MoreMenu = styled.div<{ open: boolean; wide?: boolean }>`
  position: absolute;
  bottom: ${TAB_BAR_HEIGHT + 8}px;
  right: 8px;
  background: #ffffff;
  box-shadow: 0 2px 20px rgba(78, 78, 81, 0.2);
  border-radius: 8px;
  overflow-x: hidden;
  /* Accounts + farms + seasons + nav can outgrow the screen, so the menu
     scrolls instead of running off the top of the viewport. */
  overflow-y: auto;
  max-height: calc(var(--vh-full, 100vh) - ${TAB_BAR_HEIGHT + 96}px);
  display: ${({ open }) => (open ? "flex" : "none")};
  flex-direction: column;
  /* Account rows carry an avatar, two lines of text and a badge — 160px clips
     them, so the menu widens only when that section is present. */
  min-width: ${({ wide }) => (wide ? "240px" : "160px")};
  max-width: calc(100vw - 16px);
  z-index: 1001;
`;

// The LeftBar (and with it the desktop AccountSwitcher) is hidden below the
// mobile breakpoint, so multi-account users get their switcher here instead.
const AccountRow = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  background: ${({ active }) => (active ? "#f2f7ec" : "transparent")};
  :hover {
    background: #f8f8f8;
  }
`;

const AccountSectionDivider = styled.div`
  height: 1px;
  background: rgba(137, 144, 147, 0.3);
`;

// The farm and season selectors live in the LeftBar header on desktop, which is
// hidden below the mobile breakpoint — without these sections mobile users have
// no way to switch farm or season at all.
const SectionLabel = styled.div`
  font-family: Nunito Sans, sans-serif;
  font-size: 10px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #899093;
  padding: 10px 16px 4px;
`;

const SelectRow = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: ${({ active }) => (active ? "default" : "pointer")};
  background: ${({ active }) => (active ? "#f2f7ec" : "transparent")};
  :hover {
    background: ${({ active }) => (active ? "#f2f7ec" : "#f8f8f8")};
  }
`;

const SelectDot = styled.div<{ active: boolean }>`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background: ${({ active }) => (active ? "#68ad18" : "transparent")};
  border: 1px solid ${({ active }) => (active ? "#68ad18" : "#c3c8ca")};
`;

const MoreMenuItem = styled(Link)<{ active: boolean }>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: ${({ active }) => (active ? "#68ad18" : "#494c5b")};
  font-family: Nunito Sans, sans-serif;
  font-size: 14px;
  font-weight: bold;
  text-decoration: none;
  gap: 12px;
  svg {
    width: 20px;
    height: 20px;
  }
  path {
    fill: ${({ active }) => (active ? "#68ad18" : "#899093")};
  }
  &:not(:last-child) {
    border-bottom: 1px solid rgba(137, 144, 147, 0.15);
  }
`;

const MobileTabBar: React.FC = () => {
  const lang = useLang();
  const location = useLocation();
  const { accountStore } = useStores();
  // Same rules as the desktop sidebar and the route guard (utils/navigation).
  const can = (route: ROUTES) => canOpenRoute(accountStore, route);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const showAccounts = canSwitchAccounts(accountStore);
  const memberships = showAccounts ? membershipsWithSelf(accountStore) : [];
  const t = (lang as any).team ?? {};
  const farmsVM = useFarmsVM();
  const seasonsVM = useSeasonsVM();
  const moment = useMoment();
  // Only worth showing when there is something to switch to — a single farm or
  // season gets no value from a one-option list.
  const showFarms = !farmsVM.loading && farmsVM.farms.length > 1;
  const showSeasons = !seasonsVM.loading && seasonsVM.seasons.length > 1;
  // Same label the desktop selector uses (components/Layout/Seasons/Seasons.tsx).
  const seasonName = (season: Season): string => {
    const from = moment(season.from);
    const to = moment(season.to);
    if (from.year() !== to.year()) {
      return from.format("YYYY MMM") + " - " + to.format("YYYY MMM");
    }
    if (from.month() !== to.month()) {
      return from.format("YYYY MMM") + " - " + to.format("MMM");
    }
    return from.format("YYYY MMM DD") + " - " + to.format("DD");
  };
  // Newest first — the store keeps the backend's ascending order, so sort a copy.
  const seasons = showSeasons
    ? [...seasonsVM.seasons].sort((a, b) => b.from.getTime() - a.from.getTime())
    : [];

  useOnClickOutside(moreRef, () => setMoreOpen(false));

  const isSecondaryRoute =
    location.pathname === ROUTES.SETTINGS ||
    location.pathname === ROUTES.PROFILE ||
    location.pathname === ROUTES.ANALYTICS ||
    location.pathname === ROUTES.AGENT ||
    location.pathname.startsWith(ROUTES.REPORTS);

  const tabs = [
    { title: lang.menu.fieldMenuOption, icon: <MapIcon />, route: ROUTES.FIELDS },
    { title: lang.menu.devicesMenuOption, icon: <RouterIcon />, route: ROUTES.DEVICES },
    { title: lang.menu.cropsMenuOption, icon: <TreeIcon />, route: ROUTES.CROPS },
    { title: lang.menu.tracksMenuOption, icon: <TrackIcon />, route: ROUTES.TRACKS },
  ].filter((t) => can(t.route));

  return (
    <Root>
      {tabs.map((tab) => (
        <TabItem
          key={tab.route}
          to={tab.route}
          active={location.pathname === tab.route}
        >
          {tab.icon}
          <span>{tab.title}</span>
        </TabItem>
      ))}
      <div ref={moreRef} style={{ flex: 1, height: "100%", position: "relative" }}>
        <MoreButton
          active={isSecondaryRoute}
          onClick={() => setMoreOpen((prev) => !prev)}
        >
          <MoreIcon />
          <span>{lang.menu.settingsMenuOption}</span>
        </MoreButton>
        <MoreMenu open={moreOpen} wide={showAccounts || showFarms || showSeasons}>
          {showAccounts && (
            <>
              {memberships.map((m) => (
                <AccountRow
                  key={m.accountId}
                  active={m.accountId === accountStore.activeAccountId}
                  onClick={() => {
                    setMoreOpen(false);
                    accountStore.setActiveAccountId(m.accountId);
                  }}
                >
                  <Avatar size={26} muted={!m.isSelf}>
                    {getInitials(m.name ?? m.email)}
                  </Avatar>
                  <div style={{ overflow: "hidden", flex: 1 }}>
                    <Text size={13} type="heading" color="#494C5B" nowrap>
                      {m.isSelf ? t.myAccount ?? "My account" : m.name ?? m.email}
                    </Text>
                    <Text size={11} color="#899093" nowrap>
                      {m.isSelf ? m.email : m.groupName ?? m.email}
                    </Text>
                  </div>
                  {!m.isSelf && <RoleBadge>{t.memberBadge ?? "Member"}</RoleBadge>}
                </AccountRow>
              ))}
              <AccountSectionDivider />
            </>
          )}
          {showFarms && (
            <>
              <SectionLabel>{lang.menu.farmTitle}</SectionLabel>
              {farmsVM.farms.map((f) => {
                const active = f.id === farmsVM.currentFarmId;
                return (
                  <SelectRow
                    key={f.id}
                    active={active}
                    onClick={() => {
                      setMoreOpen(false);
                      if (!active) farmsVM.setCurrentFarmId(f.id);
                    }}
                  >
                    <SelectDot active={active} />
                    <Text
                      size={14}
                      type="heading"
                      color={active ? "#68ad18" : "#494C5B"}
                      nowrap
                    >
                      {f.name}
                    </Text>
                  </SelectRow>
                );
              })}
              <AccountSectionDivider />
            </>
          )}
          {showSeasons && (
            <>
              <SectionLabel>{lang.menu.sesonTitle}</SectionLabel>
              {seasons.map((s) => {
                const active = s.id === seasonsVM.currentSeasonId;
                return (
                  <SelectRow
                    key={s.id}
                    active={active}
                    onClick={() => {
                      setMoreOpen(false);
                      if (!active) seasonsVM.setCurrentSeasonId(s.id);
                    }}
                  >
                    <SelectDot active={active} />
                    <Text
                      size={14}
                      type="heading"
                      color={active ? "#68ad18" : "#494C5B"}
                      nowrap
                    >
                      {seasonName(s)}
                    </Text>
                  </SelectRow>
                );
              })}
              <AccountSectionDivider />
            </>
          )}
          {can(ROUTES.AGENT) && (
            <MoreMenuItem
              to={ROUTES.AGENT}
              active={location.pathname === ROUTES.AGENT}
              onClick={() => setMoreOpen(false)}
            >
              <AgentIcon />
              {lang.menu.agentMenuOption}
            </MoreMenuItem>
          )}
          <MoreMenuItem
            to={ROUTES.SETTINGS}
            active={location.pathname === ROUTES.SETTINGS}
            onClick={() => setMoreOpen(false)}
          >
            <SettingsIcon />
            {lang.menu.settingsMenuOption}
          </MoreMenuItem>
          <MoreMenuItem
            to={ROUTES.PROFILE}
            active={location.pathname === ROUTES.PROFILE}
            onClick={() => setMoreOpen(false)}
          >
            <ProfileIcon />
            {lang.menu.profileMenuOption}
          </MoreMenuItem>
          {can(ROUTES.ANALYTICS) && (
            <MoreMenuItem
              to={ROUTES.ANALYTICS}
              active={location.pathname === ROUTES.ANALYTICS}
              onClick={() => setMoreOpen(false)}
            >
              <AnalyticsIcon />
              {lang.menu.analyticsMenuOption}
            </MoreMenuItem>
          )}
          {can(ROUTES.REPORTS) && (
            <MoreMenuItem
              to={ROUTES.REPORTS}
              active={location.pathname.startsWith(ROUTES.REPORTS)}
              onClick={() => setMoreOpen(false)}
            >
              <ReportIcon />
              {(lang.menu as any).reportsMenuOption ?? "Reports"}
            </MoreMenuItem>
          )}
        </MoreMenu>
      </div>
    </Root>
  );
};

export default observer(MobileTabBar);
